// ================================================================
// API 客戶端配置
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：Axios 客戶端配置、攔截器、錯誤處理
// ================================================================

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// API Base URL (從環境變數讀取，預設為 localhost:8000)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// 建立 Axios 實例
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 90000, // 90秒超時（因應 Vertex AI 增強需要較長時間）
  headers: {
    'Content-Type': 'application/json',
  },
});

// ================================================================
// 冷啟動等候提示 - 追蹤是否有請求久候未回應
// ================================================================
// Cloud Run scale-to-zero 冷啟動時，第一個請求要等後端開機，
// 這裡在請求「久候超過門檻」時通知訂閱者顯示等候畫面，
// 避免使用者以為系統當機了。
const COLD_START_THRESHOLD_MS = 2500;
let pendingRequestCount = 0;
let coldStartTimer: ReturnType<typeof setTimeout> | null = null;
const coldStartListeners = new Set<(waking: boolean) => void>();

const notifyColdStartListeners = (waking: boolean) => {
  coldStartListeners.forEach((listener) => listener(waking));
};

export const subscribeColdStart = (listener: (waking: boolean) => void): (() => void) => {
  coldStartListeners.add(listener);
  return () => {
    coldStartListeners.delete(listener);
  };
};

const onRequestStart = () => {
  pendingRequestCount += 1;
  if (!coldStartTimer) {
    coldStartTimer = setTimeout(() => {
      if (pendingRequestCount > 0) {
        notifyColdStartListeners(true);
      }
    }, COLD_START_THRESHOLD_MS);
  }
};

const onRequestEnd = () => {
  pendingRequestCount = Math.max(0, pendingRequestCount - 1);
  if (pendingRequestCount === 0) {
    if (coldStartTimer) {
      clearTimeout(coldStartTimer);
      coldStartTimer = null;
    }
    notifyColdStartListeners(false);
  }
};

// ================================================================
// Request Interceptor - 自動加入 Token
// ================================================================
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    onRequestStart();

    const requestUrl = config.url || '';

    // 根據 API 路徑選擇正確的 Token
    const isAdminApi = requestUrl.includes('/api/admin');
    const isTeacherApi = requestUrl.includes('/api/teacher');
    const token = isAdminApi
      ? localStorage.getItem('admin_token')
      : isTeacherApi
      ? localStorage.getItem('teacher_token')
      : localStorage.getItem('auth_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    onRequestEnd();
    return Promise.reject(error);
  }
);

// ================================================================
// Response Interceptor - 統一錯誤處理
// ================================================================
apiClient.interceptors.response.use(
  (response) => {
    onRequestEnd();
    // 成功響應直接返回 data
    return response.data;
  },
  (error: AxiosError) => {
    onRequestEnd();
    // 錯誤處理
    if (error.response) {
      const status = error.response.status;
      const requestUrl = error.config?.url || '';

      switch (status) {
        case 401:
          if (requestUrl.includes('/auth/login') ||
              requestUrl.includes('/auth/register') ||
              requestUrl.includes('/admin/login') ||
              requestUrl.includes('/teacher/login')) {
            // 登入 API 不自動跳轉，讓頁面顯示錯誤訊息
          } else if (requestUrl.includes('/api/admin')) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_info');
            window.location.href = '/admin/login';
          } else if (requestUrl.includes('/api/teacher')) {
            localStorage.removeItem('teacher_token');
            localStorage.removeItem('teacher_info');
            window.location.href = '/teacher/login';
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
          break;

        case 403:
          // 權限不足
          console.error('權限不足');
          break;

        case 404:
          // 資源不存在
          console.error('資源不存在');
          break;

        case 500:
          // 伺服器錯誤
          console.error('伺服器錯誤，請稍後再試');
          break;

        default:
          console.error(`錯誤: ${status}`);
      }
    } else if (error.request) {
      // 請求已發送但未收到響應 (網路錯誤)
      console.error('網路錯誤，請檢查網路連線');
    } else {
      // 其他錯誤
      console.error('請求發生錯誤');
    }

    return Promise.reject(error);
  }
);

// ================================================================
// 輔助函數
// ================================================================

/**
 * 取得完整 API URL
 */
export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`;
};

/**
 * 設定認證 Token
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
};

/**
 * 移除認證 Token
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

/**
 * 取得認證 Token
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

/**
 * 檢查是否已登入
 */
export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null;
};

export default apiClient;
